import {AssetMetadata} from '@contentlab/types/asset';
import {
  FeatureName,
  UserInput,
  UserInputCaptions,
  UserInputDub,
  UserInputReformatting,
  UserInputUpscale,
} from '@contentlab/types/project';
import {
  getResolutionFromMetadata,
  getResoultionDisplayValue,
} from '@contentlab/utils/assetUtils';

export const FEATURE_NAME_TO_METADATA_PROPERTY = {
  [FeatureName.AI_DUBBING]: 'language',
  [FeatureName.REFORMATTING]: 'aspectRatio',
  [FeatureName.UPSCALE]: 'resolution',
  [FeatureName.CAPTIONS]: 'language',
  // TODO: Note: AI_AVATAR don't have specific metadata properties yet
} as Record<FeatureName, MetadataDisplayKey>;

const defaultEmptyMetadataReturn = '-';

// Metadata display keys - all properties of AssetMetadata plus calculated properties
export type MetadataDisplayKey = keyof AssetMetadata | 'resolution';

// Helper function to get actual resolution from metadata
const getActualResolution = (metadata: AssetMetadata): string => {
  return metadata.width && metadata.height
    ? getResolutionFromMetadata(metadata)
    : defaultEmptyMetadataReturn;
};

// Helper function to get target value from userInput
const getTargetValue = (
  metadataDisplayParam: MetadataDisplayKey,
  userInput?: UserInput
): string => {
  if (!userInput) return defaultEmptyMetadataReturn;

  switch (metadataDisplayParam) {
    case 'language': {
      const userInputWithLanguage = userInput as
        | UserInputDub
        | UserInputCaptions;
      return (
        userInputWithLanguage?.language?.name || defaultEmptyMetadataReturn
      );
    }
    case 'aspectRatio': {
      const reformatInput = userInput as UserInputReformatting;
      return reformatInput?.aspectRatio || defaultEmptyMetadataReturn;
    }
    case 'resolution': {
      const upscaleInput = userInput as UserInputUpscale;
      return upscaleInput?.outputResolution
        ? getResoultionDisplayValue(upscaleInput.outputResolution)
        : defaultEmptyMetadataReturn;
    }
    default:
      return defaultEmptyMetadataReturn;
  }
};

// Helper function to get actual value from metadata
const getActualValue = (
  metadataDisplayParam: MetadataDisplayKey,
  metadata: AssetMetadata
): string => {
  switch (metadataDisplayParam) {
    case 'resolution':
      return getActualResolution(metadata);
    case 'language':
      return metadata.language || defaultEmptyMetadataReturn;
    case 'aspectRatio':
      return metadata.aspectRatio || defaultEmptyMetadataReturn;
    default:
      return (
        metadata[metadataDisplayParam]?.toString() || defaultEmptyMetadataReturn
      );
  }
};

// Method to get metadata display value in user-friendly format
export const getMetadataDisplayValue = (
  metadata: AssetMetadata,
  metadataDisplayParam?: MetadataDisplayKey,
  flowContext?: {
    status?: string;
    userInput?: UserInput;
    assetType?: 'inputAsset' | 'outputAsset';
  }
): string => {
  if (!metadataDisplayParam) {
    return defaultEmptyMetadataReturn;
  }

  // Input assets always show actual values
  if (flowContext?.assetType === 'inputAsset') {
    return getActualValue(metadataDisplayParam, metadata);
  }

  // Output assets: show target for non-ready flows, actual for ready flows
  if (flowContext?.assetType === 'outputAsset') {
    if (flowContext.status === 'ready') {
      return getActualValue(metadataDisplayParam, metadata);
    }
    return (
      getTargetValue(metadataDisplayParam, flowContext.userInput) ||
      defaultEmptyMetadataReturn
    );
  }

  // Fallback: show actual values
  return getActualValue(metadataDisplayParam, metadata);
};

export const featureNameToTitle: Record<FeatureName, string> = {
  [FeatureName.REFORMATTING]: 'Resize',
  [FeatureName.AI_DUBBING]: 'Dub',
  [FeatureName.REPHRASING]: 'Rescript',
  [FeatureName.CAPTIONS]: 'Captions',
  [FeatureName.AI_AVATAR]: 'Avatar',
  [FeatureName.UPSCALE]: 'Upscale',
};
